import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconExpand,
  Select,
  SelectSize,
} from '@ui/src';
import { DictionaryItem } from '../../../../hoc/new-application/types';
import { SourcesOfWealthTypes } from './types';
import AdditionalInfoBlock from '../additional-info-block';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';
import { CustomerStatus } from '../../../../hoc/customers/types';
import SourceOfWealthItem from './source-of-wealth';

import cn from '../aml.module.css';

const SourcesOfWealth = ({
  sourceOfWealthForIndividual,
  control,
  clearErrors,
  errors,
  status,
  setValue,
  fields,
  baseCurrency,
}: SourcesOfWealthTypes) => {
  const [
    sourceOfWealthForIndividualFiltered,
    setSourceOfWealthForIndividualFiltered,
  ] = useState<DictionaryItem[]>();
  const [totalValue, setTotalValue] = useState(0);

  useEffect(() => {
    if (sourceOfWealthForIndividual && fields?.sourcesAndSizesOfWealth) {
      setSourceOfWealthForIndividualFiltered(
        sourceOfWealthForIndividual?.filter((item) =>
          (
            fields?.sourcesAndSizesOfWealth as {
              sourceId: string;
              value: string;
            }[]
          )?.every((income) => item.id !== income.sourceId),
        ),
      );
    }
  }, [fields?.sourcesAndSizesOfWealth, sourceOfWealthForIndividual]);

  useEffect(() => {
    if (
      (
        fields?.sourcesAndSizesOfWealth as { sourceId: string; value: string }[]
      )?.filter((item) => item.sourceId)?.length
    ) {
      let totalSourceOfIncome = 0;

      (fields?.sourcesAndSizesOfWealth as { sourceId: string; value: string }[])
        ?.filter((item) => item.sourceId)
        ?.forEach((item) => {
          totalSourceOfIncome += Number(item.value);
        });

      setTotalValue(totalSourceOfIncome);
    }
  }, [fields?.sourcesAndSizesOfWealth]);

  return (
    <AdditionalInfoBlock title="Source and size of wealth">
      <RegulatedCompanyRow title="Require">
        <Controller
          control={control}
          name="requiresSourceAndSizeOfWealth"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['requiresSourceAndSizeOfWealth']);

                if (e.target.value === 'no') {
                  clearErrors([
                    'sourcesAndSizesOfWealth',
                    'sourcesAndSizesOfWealthOther',
                    'sourcesAndSizesOfWealthBorrowedOther',
                  ]);

                  setValue('sourcesAndSizesOfWealth', []);
                  setValue('sourcesAndSizesOfWealthOther', '');
                  setValue('sourcesAndSizesOfWealthBorrowedOther', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.requiresSourceAndSizeOfWealth?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.requiresSourceAndSizeOfWealth === 'yes' && (
        <>
          <div className={cn.sourceOfIncomeTitle}>
            Mandatory for high-risk cases only
          </div>
          {(fields?.sourcesOfIncomes?.length as number) > 0 && (
            <div className={cn.sourcesOfIncomeList}>
              {(
                fields?.sourcesAndSizesOfWealth as {
                  sourceId: string;
                  value: string;
                  id?: string;
                }[]
              )?.map((item) => (
                <SourceOfWealthItem
                  key={item?.id || item.sourceId}
                  sourcesOfWealthForIndividual={sourceOfWealthForIndividual}
                  sourceOfWealthForIndividualFiltered={
                    sourceOfWealthForIndividualFiltered
                  }
                  sourceOfWealthForIndividual={{
                    id: item?.id || item.sourceId,
                    ...item,
                  }}
                  sourceOfWealthForIndividualValues={
                    fields?.sourcesAndSizesOfWealth as {
                      sourceId: string;
                      value: string;
                    }[]
                  }
                  setValue={setValue}
                  baseCurrency={baseCurrency}
                  control={control}
                  errors={errors}
                  clearErrors={clearErrors}
                  status={status}
                />
              ))}
            </div>
          )}
          {(
            fields?.sourcesAndSizesOfWealth as {
              sourceId: string;
              value: string;
              id?: string;
            }[]
          )?.filter((item) => item.sourceId)?.length > 0 && (
            <div className={cn.sourceOfIncomeTotal}>
              {`Value of Wealth: ${formatBalance(totalValue, baseCurrency)} ${baseCurrency?.name}`}
            </div>
          )}
          {sourceOfWealthForIndividualFiltered &&
            sourceOfWealthForIndividualFiltered?.length > 0 &&
            status !== CustomerStatus.CLOSED && (
              <div className={cn.addSourceButtonWrapper}>
                <ButtonSecondary
                  text="Add Source"
                  icon={<IconExpand width={16} height={16} />}
                  size={ButtonSize.SMALL}
                  className={`button-white ${cn.addSourceButton}`}
                  onClick={() => {
                    const nextSourcesAndSizesOfWealth = (
                      [] as {
                        sourceId: string;
                        value: string | number;
                        id?: string;
                      }[]
                    ).concat(
                      fields?.sourcesAndSizesOfWealth as {
                        id?: string;
                        sourceId: string;
                        value: string | number;
                      }[],
                    );

                    nextSourcesAndSizesOfWealth.push({
                      id: `source-of-wealth-${uuid()}`,
                      sourceId: '',
                      value: '',
                    });

                    setValue(
                      'sourcesAndSizesOfWealth',
                      nextSourcesAndSizesOfWealth,
                    );
                  }}
                />
              </div>
            )}
        </>
      )}
      <Controller
        control={control}
        name="sourcesAndSizesOfWealth"
        defaultValue={[]}
        render={({ field: { name } }) => <input type="hidden" name={name} />}
      />
      {errors?.sourcesAndSizesOfWealth?.message && (
        <div className={cn.stepOneQuestionnaireError}>
          {errors?.sourcesAndSizesOfWealth?.message}
        </div>
      )}
    </AdditionalInfoBlock>
  );
};

export default SourcesOfWealth;
